'use client';

import { useAuth } from '@clerk/nextjs';
import type { OnApproveData } from '@paypal/paypal-js';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/icon';
import { MaxWidthWrapper } from '@/components/max-width-wrapper';
import { capturePayPalOrder, createPayPalOrder, createPayPalSubscription, createStripeCheckoutSession, getPayPalSubscription } from '@/features/billing/actions';
import { PaymentFormAmount } from '@/features/billing/components/payment-form/form-amount';
import { PaymentFormFrequency } from '@/features/billing/components/payment-form/form-frequency';
import { PaymentFormSummary } from '@/features/billing/components/payment-form/form-summary';
import { PaymentFormType } from '@/features/billing/components/payment-form/form-type';
import { PaymentPaypalButtons } from '@/features/billing/components/payment-form/paypal-buttons';
import { COST_PER_PERSON, DEFAULT_PAYMENT_AMOUNT } from '@/features/billing/config';
import { capture } from '@/features/tracking';
import { env } from '@/lib/env';
import { useOnboarding } from './onboarding-context';
const PAYPAL_CLIENT_ID = env.NEXT_PUBLIC_PAYPAL_CLIENT_ID;
export function StepPayItForward() {
  const {
    isSignedIn
  } = useAuth();
  const router = useRouter();
  const {
    complete
  } = useOnboarding();
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentRecurring, setPaymentRecurring] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentType, setPaymentType] = useState<'stripe' | 'paypal'>('stripe');
  const [paymentAcceptTerms, setPaymentAcceptTerms] = useState(true);
  const [isFree, setIsFree] = useState(true);
  const paymentAmountRef = useRef(0);
  paymentAmountRef.current = paymentAmount;
  const hasTracked = useRef(false);
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    capture('donation:pay_it_forward_modal_view', {
      trigger_point: 'payitforward'
    });
  }, []);
  const handleContinueFree = async () => {
    capture('donation:continue_free_click', {});
    setPaymentSubmitting(true);
    await complete();
  };
  const handlePaymentTypeChange = (newPaymentType: 'stripe' | 'paypal') => {
    setPaymentType(newPaymentType);
  };
  const handleStripePayment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isFree) {
      await handleContinueFree();
      return;
    }
    if (paymentType === 'stripe') {
      setPaymentSubmitting(true);
      await createStripeCheckoutSession({
        isRecurring: paymentRecurring,
        amount: paymentAmount * 100
      });
    }
  };
  const paypalCreateOrder = async () => {
    const response = await createPayPalOrder(paymentAmountRef.current);
    return response?.order.id ?? '';
  };
  const paypalOnApprove = async (data: OnApproveData) => {
    if (!data.orderID) return;
    const response_0 = await capturePayPalOrder(data.orderID);
    if (response_0) {
      const successUrl = `/pay-it-forward/success/?success=true&gateway=paypal&amount=${paymentAmount}&transaction_id=${data.orderID}`;
      if (isSignedIn) {
        router.push(successUrl);
      } else {
        const loginHint = response_0.payerEmail ? `&login_hint=${encodeURIComponent(response_0.payerEmail)}` : '';
        router.push(`/sign-in?redirect_url=${encodeURIComponent(successUrl)}${loginHint}`);
      }
    }
  };
  const paypalCreateSubscription = async () => {
    const response_1 = await createPayPalSubscription(paymentAmountRef.current);
    return response_1?.subscription.id ?? '';
  };
  const paypalOnApproveSubscription = async (data_0: OnApproveData) => {
    if (!data_0.subscriptionID) return;
    const getSubscriptionAPI = await getPayPalSubscription(data_0.subscriptionID);
    if (getSubscriptionAPI.success) {
      const subscriptionId = getSubscriptionAPI.data.subscription.id;
      const subscriberEmail = getSubscriptionAPI.data.subscription.subscriber?.email_address;
      const successUrl_0 = `/pay-it-forward/success/?success=true&gateway=paypal&amount=${paymentAmount}&subscription=true&transaction_id=${subscriptionId}`;
      if (isSignedIn) {
        router.push(successUrl_0);
      } else {
        const loginHint_0 = subscriberEmail ? `&login_hint=${encodeURIComponent(subscriberEmail)}` : '';
        router.push(`/sign-in?redirect_url=${encodeURIComponent(successUrl_0)}${loginHint_0}`);
      }
    }
  };
  const paypalOnError = () => {
    router.push(`/pay-it-forward/canceled/?canceled=true&gateway=paypal&amount=${paymentAmount}&subscription=${paymentRecurring}`);
  };
  return <div className='flex flex-1 flex-col overflow-hidden'>
      <form className='h-full flex-1 overflow-y-auto pt-8 pb-24' data-lenis-prevent noValidate onSubmit={handleStripePayment}>
        <MaxWidthWrapper size='lg'>
          <div>
            <div className='mb-16 text-center'>
              <h2 className='font-semibold text-[3.75rem] uppercase leading-none max-[440px]:text-[3rem]'>
                Pay It Forward
              </h2>
              <p className='mt-2 font-bold text-[1.375rem] uppercase tracking-[0.04rem] max-[440px]:text-base'>
                Keep the film free and uncensored
              </p>
            </div>

            <PaymentFormFrequency paymentRecurring={paymentRecurring} setPaymentRecurring={setPaymentRecurring} setPaymentType={setPaymentType} />

            <PaymentFormAmount paymentRecurring={paymentRecurring} setPaymentAmount={setPaymentAmount} showFreeOption={true} isFree={isFree} setIsFree={setIsFree} defaultAmount={DEFAULT_PAYMENT_AMOUNT} costPerPerson={COST_PER_PERSON} />

            <div>
              {!isFree && <>
                  <PaymentFormType paymentType={paymentType} setPaymentType={handlePaymentTypeChange} />

                  <PaymentFormSummary paymentAmount={paymentAmount} paymentRecurring={paymentRecurring} paymentType={paymentType} />
                </>}

              <div className='mt-4'>
                {!isFree && <fieldset className='mb-14'>
                    <input id='payment-accept-terms' type='checkbox' checked={paymentAcceptTerms} onChange={() => setPaymentAcceptTerms(!paymentAcceptTerms)} />
                    <label className='ml-2 text-sm tracking-[0.02rem]' htmlFor='payment-accept-terms'>
                      I agree to the{' '}
                      <a className='underline' href='/terms' target='_blank' rel='noopener noreferrer'>
                        terms and conditions
                      </a>
                    </label>
                  </fieldset>}

                <PayPalScriptProvider options={{
                clientId: PAYPAL_CLIENT_ID,
                currency: 'USD',
                debug: false
              }}>
                  <PaymentPaypalButtons paymentAmount={paymentAmount} paymentRecurring={paymentRecurring} paymentType={paymentType} paymentSubmitting={paymentSubmitting} paymentAcceptTerms={paymentAcceptTerms} paypalCreateOrder={paypalCreateOrder} paypalOnApprove={paypalOnApprove} paypalCreateSubscription={paypalCreateSubscription} paypalOnApproveSubscription={paypalOnApproveSubscription} paypalOnError={paypalOnError} isFree={isFree} />
                </PayPalScriptProvider>

                {paymentType === 'stripe' && !isFree && <div className='mt-4 text-center leading-none'>
                    <button type='submit' className='ease w-full bg-christ-white-900 px-8 py-8 pb-7 font-bold text-[1.3rem] text-christ-black-900 uppercase tracking-[0.02rem] opacity-90 transition-all duration-300 hover:opacity-100 disabled:opacity-50' disabled={paymentSubmitting || !paymentAcceptTerms}>
                      {paymentSubmitting ? 'Please wait...' : 'Checkout'}
                    </button>
                    <span className='mt-3 flex items-center justify-center gap-2 text-xs tracking-[0.02rem]'>
                      <Icon name='lock' className='size-4' /> Secure Payment
                    </span>
                  </div>}

                {isFree && <div className='mt-4 text-center leading-none'>
                    <button type='submit' className='ease w-full bg-christ-white-900 px-8 py-8 pb-7 font-bold text-[1.3rem] text-christ-black-900 uppercase tracking-[0.02rem] opacity-90 transition-all duration-300 hover:opacity-100 disabled:opacity-50' disabled={paymentSubmitting}>
                      {paymentSubmitting ? 'Please wait...' : 'Continue for Free'}
                    </button>
                    <span className='mt-4 block font-bold text-[1.275rem] uppercase tracking-[0.04rem] max-[640px]:text-base max-[640px]:leading-normal'>
                      You have 48hrs to watch.{' '}
                      <span className='max-[640px]:block'>Pay it Forward to own.</span>
                    </span>
                  </div>}
              </div>
            </div>
          </div>
        </MaxWidthWrapper>
      </form>
    </div>;
}